import usePageTitle from "../../core/hooks/UsePageTitle";
import DefaultLayout from "../layouts/default/Default.layout";
import {
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Button,
  Radio,
  RadioChangeEvent,
  Select,
  Upload,
  Spin,
  Modal,
  notification,
  message,
} from "antd";
import { CopyOutlined, PictureOutlined, DeleteOutlined } from "@ant-design/icons";
import React, { useCallback, useEffect, useReducer, useRef, useState } from "react";
import useUsuario from "../../core/hooks/UseUsuario";
import useBancas from '../../core/hooks/UseBanca';
import useMaterias from '../../core/hooks/UseMaterias';
import QuestaoService from "../../sdk/services/QuestaoService";
import FileService from "../../sdk/services/FileService";
import PdfHighlightService from "../../sdk/services/PdfHighlightService";
import SharedTextService from "../../sdk/services/SharedTextService";
import { Questao } from "../../sdk/@types";
import { PdfHighlight, SharedText } from "../../sdk/@types/Acertei";
import SharedTextEditorModal from "../components/SharedTextEditor/SharedTextEditorModal";
import ImageCropModal from "../components/ImageCropModal/ImageCropModal";
import useQuestao from "../../core/hooks/useQuestao";
import MarkdownEditor from "../components/MarkdownEditor";
import PdfAnnotator from "../components/PdfAnnotator/PdfAnnotator";
import PdfViewer from "../components/PdfViewer/PdfViewer";
import { useLocation } from "react-router-dom";
import { TagDto } from "../../sdk/@types/TagDto";
import QuestionPreviewModal, { QuestionPreviewData } from "../components/QuestionPreview/QuestionPreviewModal";

const { TextArea } = Input;
const { Option } = Select;

// A quinta alternativa (E) é opcional — as quatro primeiras continuam obrigatórias.
const MAX_CHOICES = 5;
// Imagem enviada antes de a questão nova existir — sobrevive a um reload.
const DRAFT_ATTACHMENT_KEY = "questao.draft.attachment";
const NOMES_DAS_ALTERNATIVAS = ["Um", "Dois", "Três", "Quatro", "Cinco"];
const MAX_TAG_LENGTH = 40;

export default function QuestaoForm() {
  const [form] = Form.useForm();
  const [reducerValue, forceUpdate] = useReducer((x) => x + 1, 0);
  const choices = Form.useWatch("choices", form);
  const statement = Form.useWatch("statement", form);

  const [tagsKeywords, setTagsKeywords] = React.useState<TagDto.TagDto[]>([]);
  const [showRationale, setShowRationale] = useState(false);
  const [showOutdated, setShowOutdated] = useState(false);
  // Alternativa E é opcional: só existe quando o autor a adiciona ou quando a questão já foi salva com ela.
  const [temAlternativaE, setTemAlternativaE] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState<QuestionPreviewData | null>(null);
  // Última lista de tags aceita — usada para distinguir, no onChange do Select, a tag
  // recém-adicionada (a validar) das já aceitas anteriormente.
  const prevKeywordsRef = useRef<string[]>([]);

  useEffect(() => {
    QuestaoService.buscarTagsDasPalavrasChaves().then((resultado) => {
      setTagsKeywords(resultado);
    });    
  }, [reducerValue]);

  const { bancas, fetchBancas } = useBancas();
  const { materias, fetchMaterias } = useMaterias();

  const { usuario } = useUsuario();

  const { questao, newQuestion } = useQuestao();

  const location = useLocation();
  const [questaoReturned, setQuestaoReturned] = useState<Questao.Questao | null>(location.state?.questao ?? null);

  usePageTitle("Usuário");

  const opcoesDeDificuldade = [
    { label: "DIFÍCIL", value: 'Difícil' },
    { label: "MÉDIA", value: 'Média' },
    { label: "FÁCIL", value: 'Fácil' },
  ];

  const [imageUrl, setImageUrl] = useState("");
  const [imageName, setImageName] = useState("");
  const [uploading, setUploading] = useState(false);

  const [highlight, setHighlight] = useState<PdfHighlight | null>(null);
  const [annotatorOpen, setAnnotatorOpen] = useState(false);
  const [pdfVisible, setPdfVisible] = useState(false);

  const [sharedTexts, setSharedTexts] = useState<SharedText[]>([]);
  const [sharedTextModalOpen, setSharedTextModalOpen] = useState(false);
  const [editingSharedText, setEditingSharedText] = useState<SharedText | null>(null);
  const sharedTextId = Form.useWatch("sharedTextId", form) as string | undefined;
  const sharedTextSelecionado = sharedTexts.find((t) => t.id === sharedTextId) ?? null;

  useEffect(() => {
    SharedTextService.list()
      .then(setSharedTexts)
      .catch(() => setSharedTexts([]));
  }, []);

  // Um texto novo já entra selecionado; um editado só atualiza a lista.
  const handleSharedTextSaved = (saved: SharedText) => {
    setSharedTexts((prev) => {
      const exists = prev.some((t) => t.id === saved.id);
      return exists ? prev.map((t) => (t.id === saved.id ? saved : t)) : [saved, ...prev];
    });
    if (!editingSharedText) {
      form.setFieldsValue({ sharedTextId: saved.id, sharedTextOrder: 1 });
    }
    setSharedTextModalOpen(false);
    setEditingSharedText(null);
  };

  const handleImageUpload = async (file: File) => {
    const oldKey = imageName;
    setUploading(true);
    try {
      const { file_key } = await FileService.upload(file, 'question_image');
      const base = process.env.REACT_APP_AUTH_SERVER_BASE_URL ?? '';
      setImageUrl(`${base}/files/public/${file_key}`);
      setImageName(file_key);
      if (oldKey) FileService.delete(oldKey).catch(() => {});

      // Persistência imediata: um reload não pode perder a imagem escolhida.
      // Questão existente grava o anexo agora (PUT cirúrgico — o PUT completo
      // zeraria os campos não enviados); questão nova guarda o rascunho no
      // localStorage até o cadastro.
      const qid = String((questaoReturned || questao)?.id ?? '');
      if (qid && qid !== 'undefined') {
        try {
          await QuestaoService.setQuestaoAttachment(qid, file_key);
        } catch {
          notification.warning({
            message: 'Imagem enviada, mas ainda não vinculada.',
            description: 'Salve o formulário para gravar o vínculo na questão.',
          });
        }
      } else {
        try {
          localStorage.setItem(DRAFT_ATTACHMENT_KEY, file_key);
        } catch {
          // Storage bloqueado: segue só em memória, como antes.
        }
      }
    } catch {
      notification.error({ message: 'Erro ao enviar imagem.' });
    } finally {
      setUploading(false);
    }
  };

  // ------- importar / colar / recortar imagem -------
  // Toda imagem entra pelo modal de recorte; "Usar inteira" pula o recorte.
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [cropFile, setCropFile] = useState<File | null>(null);
  // true quando o modal abriu com a imagem JÁ enviada (Ajustar): aplicar sem
  // mudança nenhuma vira no-op; um arquivo novo (colar/Trocar) sempre sobe.
  const [cropIsCurrent, setCropIsCurrent] = useState(false);

  const openCropWith = useCallback((file: File, opts?: { current?: boolean }) => {
    setCropSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
    setCropFile(file);
    setCropIsCurrent(opts?.current === true);
  }, []);

  const closeCrop = useCallback(() => {
    setCropSrc((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setCropFile(null);
    setCropIsCurrent(false);
  }, []);

  // O backend só aceita PNG/JPEG — outros formatos (print colado como WEBP,
  // GIF...) são convertidos para PNG antes de subir.
  const ensurePngOrJpeg = async (file: File): Promise<File | null> => {
    if (file.type === "image/png" || file.type === "image/jpeg") return file;
    try {
      const bitmap = await createImageBitmap(file);
      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      canvas.getContext("2d")?.drawImage(bitmap, 0, 0);
      const png = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/png"),
      );
      if (!png) return null;
      return new File([png], file.name.replace(/\.\w+$/, "") + ".png", {
        type: "image/png",
      });
    } catch {
      return null;
    }
  };

  const handleCropDone = async (blob: Blob | null) => {
    const original = cropFile;
    closeCrop();
    if (!original) return;

    const existingKey = imageName;

    // Ajustar sem mudança nenhuma (sem recorte, escala 100%): nada a gravar.
    // Arquivo NOVO (colar/Trocar) aplicado inteiro ainda precisa subir.
    if (!blob && cropIsCurrent) return;

    const toUpload = blob
      ? new File(
          [blob],
          (original.name.replace(/\.\w+$/, "") || "imagem") + ".png",
          { type: "image/png" },
        )
      : await ensurePngOrJpeg(original);
    if (!toUpload) {
      notification.error({ message: "Formato de imagem não suportado." });
      return;
    }

    if (existingKey) {
      // Atualiza o MESMO arquivo — a chave (e a URL usada no enunciado) não
      // muda, então nada mais precisa ser persistido na questão.
      setUploading(true);
      try {
        await FileService.replace(existingKey, toUpload);
        const base = process.env.REACT_APP_AUTH_SERVER_BASE_URL ?? "";
        // Mesmo caminho, conteúdo novo: o ?v= fura o cache do navegador.
        setImageUrl(`${base}/files/public/${existingKey}?v=${Date.now()}`);
        notification.success({ message: "Imagem atualizada." });
      } catch (error) {
        const status = (error as any)?.response?.status;
        const apiMsg = (error as any)?.response?.data?.error;
        console.error("replace imagem:", status, error);
        notification.error({
          message: "Erro ao atualizar a imagem.",
          description:
            status === 404 || status === 405
              ? "O backend em execução ainda não tem a rota PUT /files — reinicie a API com o código novo."
              : apiMsg ?? (status ? `HTTP ${status}` : undefined),
        });
      } finally {
        setUploading(false);
      }
      return;
    }

    await handleImageUpload(toUpload);
  };

  // Ctrl+V em qualquer ponto da tela (fora de campos de texto) importa o
  // print direto — o caminho natural de quem captura questão de PDF/prova.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)
      ) {
        return;
      }
      const item = Array.from(e.clipboardData?.items ?? []).find((i) =>
        i.type.startsWith("image/"),
      );
      const file = item?.getAsFile();
      if (file) {
        e.preventDefault();
        openCropWith(file);
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [openCropWith]);

  // Reabre a imagem já enviada no modal de ajuste (busca o blob primeiro —
  // canvas com URL remota de outra origem ficaria tainted). A rota pública
  // recusa anexos antigos (403 quando o entity_type não é question_image,
  // 404 quando o arquivo é anterior à coleção Files), então cai para a rota
  // autenticada antes de desistir.
  const ajustarExistente = async () => {
    if (!imageUrl) return;
    try {
      let blob: Blob | null = null;
      try {
        const resp = await fetch(imageUrl);
        if (resp.ok && (resp.headers.get("content-type") ?? "").startsWith("image/")) {
          blob = await resp.blob();
        }
      } catch {
        // CORS/rede: tenta a rota autenticada abaixo.
      }
      if (!blob && imageName) {
        const authed = await FileService.download(imageName);
        if (authed?.type?.startsWith("image/")) blob = authed;
      }
      if (!blob) throw new Error("imagem indisponível nas duas rotas");
      openCropWith(
        new File([blob], imageName || "imagem.png", {
          type: blob.type || "image/png",
        }),
        { current: true },
      );
    } catch (error) {
      console.error("ajustar imagem: falha ao carregar", error);
      notification.error({
        message: "Não foi possível carregar a imagem para ajuste.",
        description: "Use Trocar para enviar o arquivo de novo, se persistir.",
      });
    }
  };

  useEffect(() => {
    const questaoInicial = questaoReturned || questao;
    if (questaoInicial?.attachment) {
      const base = process.env.REACT_APP_AUTH_SERVER_BASE_URL ?? '';
      setImageUrl(`${base}/files/public/${questaoInicial.attachment}`);
      setImageName(questaoInicial.attachment);
    } else if (questaoInicial?.attachmentUrl) {
      setImageUrl(questaoInicial.attachmentUrl);
    } else if (!questaoInicial) {
      // Questão nova: restaura a imagem enviada antes de um reload.
      try {
        const draft = localStorage.getItem(DRAFT_ATTACHMENT_KEY);
        if (draft) {
          const base = process.env.REACT_APP_AUTH_SERVER_BASE_URL ?? '';
          setImageUrl(`${base}/files/public/${draft}`);
          setImageName(draft);
        }
      } catch {
        // Storage bloqueado: sem rascunho para restaurar.
      }
    }
  }, [questaoReturned, questao]);

  const questionId = String((questaoReturned || questao)?.id ?? '');

  useEffect(() => {
    if (!questionId || questionId === 'undefined') { setHighlight(null); return; }
    PdfHighlightService.getByQuestion(questionId)
      .then(h => setHighlight(h ?? null))
      .catch(() => setHighlight(null));
  }, [questionId]);

  useEffect(() => {
    const questaoInicial = questaoReturned || questao;

    const choicesFromQuestao = questaoInicial?.choices || [];

    // A questão pode ter sido salva com 4 ou 5 alternativas — nunca descarte a quinta.
    const totalDeAlternativas = Math.min(Math.max(4, choicesFromQuestao.length), MAX_CHOICES);
    setTemAlternativaE(totalDeAlternativas === MAX_CHOICES);

    const baseChoices = Array.from({ length: totalDeAlternativas }).map((_, i) => ({
      description: "",
      correct: i === 0,
      authorComment: "",
    }));

    // merge
    const choices = baseChoices.map((base, i) => ({
      ...base,
      ...choicesFromQuestao[i],
    }));

    const keywordsTreated = questaoInicial?.keyword ? questaoInicial.keyword.split(",").map((keyword) => keyword.trim()) : [];

    const valoresIniciais = {
      ...questaoInicial,
      choices,
      keyword: keywordsTreated,
      outdated: questaoInicial?.outdated || "",
      subjectId: questaoInicial?.subjectId || questaoInicial?.subject?.id, // ensure correct field
      testingAgencyId: questaoInicial?.testingAgencyId || questaoInicial?.testingAgency?.id,

    };

    if (questaoInicial?.outdated){
      setShowOutdated(true);
    }

    form.setFieldsValue(valoresIniciais);
    // Sobrescreve o array inteiro: setFieldsValue faria merge por índice e poderia
    // manter uma alternativa E de uma questão carregada anteriormente.
    form.setFields([{ name: "choices", value: choices }]);
    prevKeywordsRef.current = keywordsTreated;
  }, [questaoReturned, questao, form]);


  useEffect(() => {
    fetchBancas();
    fetchMaterias();
  }, [fetchBancas, fetchMaterias]);

  const toggleRationale = () => {
    setShowRationale(prev => !prev);
  }

  const toggleOutdated = () => {
    setShowOutdated(prev => !prev);
  }

  const numberToLetter = (index : number) : string => {
    return String.fromCharCode(97 + index);
  }

  // setFields (e não setFieldsValue) porque o antd faz merge por índice em arrays:
  // um array menor não apagaria a alternativa E do store do formulário.
  const definirAlternativas = (novasChoices: any[]) => {
    form.setFields([{ name: "choices", value: novasChoices }]);
  }

  const adicionarAlternativaE = () => {
    const atuais = form.getFieldValue("choices") || [];
    definirAlternativas([
      ...atuais.slice(0, MAX_CHOICES - 1),
      { description: "", correct: false, authorComment: "" },
    ]);
    setTemAlternativaE(true);
  }

  const removerAlternativaE = () => {
    const atuais = form.getFieldValue("choices") || [];
    const restantes = atuais.slice(0, MAX_CHOICES - 1);
    // Se a E era a correta, a questão ficaria sem gabarito — devolve para a A.
    if (!restantes.some((choice: any) => choice?.correct)) {
      restantes[0] = { ...restantes[0], correct: true };
    }
    definirAlternativas(restantes);
    setTemAlternativaE(false);
  }

  // Select mode="tags" não tem validador por token: a cada onChange, o rc-field-form já
  // gravou o array novo no form antes de chamar este handler, então a comparação com a
  // lista anterior precisa vir de uma ref própria, não de form.getFieldValue.
  const handleKeywordsChange = useCallback((value: string[]) => {
    const previous = prevKeywordsRef.current;
    const rejected = value.filter((tag) => tag.length > MAX_TAG_LENGTH && !previous.includes(tag));

    if (rejected.length > 0) {
      const sanitized = value.filter((tag) => !rejected.includes(tag));
      form.setFieldValue(["keyword"], sanitized);
      prevKeywordsRef.current = sanitized;
      message.warning(`Tags não podem ter mais de ${MAX_TAG_LENGTH} caracteres.`);
      return;
    }

    prevKeywordsRef.current = value;
  }, [form]);

  // Lê o rascunho atual do form (não o último salvo) — é o que torna o preview
  // útil para uma questão ainda sem id ou com edições não enviadas.
  const buildPreviewData = useCallback((): QuestionPreviewData | null => {
    const values = form.getFieldsValue();
    const statement: string = (values.statement ?? "").trim();
    const formChoices: Array<{ description?: string; correct?: boolean; rationale?: string }> = values.choices ?? [];
    const filledChoices = formChoices.filter(
      (choice) => (choice?.description ?? "").trim().length > 0,
    );

    if (!statement || filledChoices.length < 2) {
      return null;
    }

    const materia = materias.find((m: any) => m.id === values.subjectId);
    const banca = bancas.find((b: any) => b.id === values.testingAgencyId);
    const tags: string[] = Array.isArray(values.keyword) ? values.keyword : [];

    return {
      statement: values.statement,
      choices: filledChoices.map((choice) => ({
        description: choice.description ?? "",
        correct: choice.correct ?? false,
        rationale: choice.rationale ?? "",
      })),
      subjectName: materia?.name,
      agencyName: banca?.name,
      agencyYear: banca?.year,
      tags,
      sharedText: sharedTextSelecionado
        ? {
            id: sharedTextSelecionado.id,
            title: sharedTextSelecionado.title,
            body: sharedTextSelecionado.body,
            source: sharedTextSelecionado.source,
          }
        : undefined,
    };
  }, [form, materias, bancas, sharedTextSelecionado]);

  const filledChoicesCount = (choices ?? []).filter(
    (choice: any) => (choice?.description ?? "").trim().length > 0,
  ).length;
  const previewDisabled = !(statement ?? "").trim() || filledChoicesCount < 2;

  const openPreview = () => {
    const data = buildPreviewData();
    if (!data) {
      notification.info({
        message: "Preencha o enunciado e ao menos duas alternativas para pré-visualizar",
      });
      return;
    }
    setPreviewData(data);
    setPreviewOpen(true);
  };

  return (
    <DefaultLayout>
      <div className="flex" style={{ height: 'calc(100vh - 80px)' }}>
      <div className={`${pdfVisible && highlight ? 'flex-1 min-w-0' : 'w-full'} overflow-y-auto bg-slate-50 dark:bg-[#0a0f0d] p-4`}>
        <Form
          form={form}
          onFinish={async (question: Questao.Questao) => {
            try {
              question.userId = "68695462b002469fae69e32e";
              question.keyword = question.keyword?.toString();
              question.attachment = imageName;
              // Alternativa em branco (a E não preenchida, por exemplo) não é salva.
              question.choices = question.choices?.filter((choice) =>
                choice.description?.trim(),
              );

              if (questaoReturned?.id){
                question.id = questaoReturned.id;
                await QuestaoService.atualizarQuestao(question).then((response) => {
                  notification.info({
                    message: "Sucesso",
                    description: "Questão atualizada com sucesso.",
                  });
                });
              }else{
                await QuestaoService.inserirNovaQuestao(question).then((response) => {
                  notification.success({
                    message: "Sucesso",
                    description: "Questão cadastrada com sucesso.",
                  });
                });
              }

              // A imagem virou anexo da questão salva — o rascunho já cumpriu o papel.
              try { localStorage.removeItem(DRAFT_ATTACHMENT_KEY); } catch {}
              setImageUrl('');
              setImageName('');

              form.resetFields();
              newQuestion();
              setQuestaoReturned(questao);
            } catch (error) {
              /* if (error instanceof CustomError) {
                if (error.data?.objects) {
                  form.setFields(
                    error.data.objects.map((error) => {
                      return {
                        name: error.name?.split(
                          '.'
                        ) as string[],
                        errors: [error.userMessage],
                      };
                    })
                  );
                }
              } else {
                notification.error({
                  message: 'Houve um erro',
                });
              }*/
              notification.error({
                message: "Houve um erro",
              });
            }
          }}
          layout={"vertical"}
          autoComplete={"off"}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={"Banca"} name={["testingAgencyId"]}>
                <Select
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option!.children as unknown as string).includes(input)
                  }
                >
                  {bancas.map((banca) => (
                    <Select.Option key={banca.id} value={banca.id}>
                      {banca.year} - {banca.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={"Matéria"} name={["subjectId"]}>
                <Select
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option!.children as unknown as string).includes(input)
                  }
                  /*filterSort={(optionA, optionB) =>
                    (optionA!.children as unknown as string)
                      .toLowerCase()
                      .localeCompare(
                        (
                          optionB!
                            .children as unknown as string
                        ).toLowerCase()
                      )
                  }*/
                >
                  {materias.map((materia) => (
                    <Select.Option key={materia.id} value={materia.id}>
                      {materia.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <div className="mb-4">
                <p className="text-sm font-medium mb-2" style={{ color: '#6b8f6e' }}>
                  Imagem do Enunciado
                </p>

                {!imageUrl && !uploading && (
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={file => { openCropWith(file); return false; }}
                  >
                    <div className="border-2 border-dashed border-[#1a2e1c] hover:border-[#a3e635] rounded-lg p-8 text-center cursor-pointer transition-colors group">
                      <PictureOutlined style={{ fontSize: 28, color: '#6b8f6e', display: 'block', marginBottom: 8 }} />
                      <p className="text-sm m-0" style={{ color: '#6b8f6e' }}>
                        Clique, arraste — ou cole um print com Ctrl+V
                      </p>
                      <p className="text-xs mt-1 m-0" style={{ color: '#6b8f6e', opacity: 0.6 }}>
                        PNG ou JPG (outros formatos são convertidos). Dá para recortar antes de salvar.
                      </p>
                    </div>
                  </Upload>
                )}

                {uploading && (
                  <div className="border-2 border-dashed border-[#1a2e1c] rounded-lg p-8 flex items-center justify-center gap-2">
                    <Spin size="small" />
                    <span className="text-sm" style={{ color: '#6b8f6e' }}>Enviando imagem...</span>
                  </div>
                )}

                {imageUrl && !uploading && (
                  <div className="border border-[#1a2e1c] rounded-lg overflow-hidden bg-[#0a0f0d]">
                    <div className="flex justify-center p-1.5 bg-[#0f1a10]">
                      <img
                        src={imageUrl}
                        alt="Imagem do enunciado"
                        className="max-w-full rounded"
                        style={{ maxHeight: 280, objectFit: 'contain' }}
                      />
                    </div>
                    <div className="flex items-center justify-between px-3 py-2 border-t border-[#1a2e1c] bg-[#0a0f0d]">
                      <span className="flex items-center gap-1.5 text-xs text-green-700 dark:text-[#82ca9d]">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-700 dark:bg-[#82ca9d] inline-block" />
                        Vinculada ao enunciado
                      </span>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          className="text-xs flex items-center gap-1 transition-colors hover:text-[#a3e635]"
                          style={{ color: '#6b8f6e', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                          onClick={() => navigator.clipboard.writeText(imageUrl)}
                        >
                          <CopyOutlined style={{ fontSize: 11 }} /> Copiar URL
                        </button>
                        <button
                          type="button"
                          className="text-xs transition-colors hover:text-[#a3e635]"
                          style={{ color: '#6b8f6e', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                          onClick={ajustarExistente}
                        >
                          Ajustar
                        </button>
                        <Upload
                          accept="image/*"
                          showUploadList={false}
                          beforeUpload={file => { openCropWith(file); return false; }}
                        >
                          <button
                            type="button"
                            className="text-xs transition-colors hover:text-[#a3e635]"
                            style={{ color: '#6b8f6e', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                          >
                            Trocar
                          </button>
                        </Upload>
                        <button
                          type="button"
                          className="text-xs flex items-center gap-1"
                          style={{ color: '#f87171', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                          onClick={() => {
                            if (imageName) FileService.delete(imageName).catch(() => {});
                            const qid = String((questaoReturned || questao)?.id ?? '');
                            if (qid && qid !== 'undefined') {
                              QuestaoService.setQuestaoAttachment(qid, '').catch(() => {});
                            }
                            try { localStorage.removeItem(DRAFT_ATTACHMENT_KEY); } catch {}
                            setImageUrl('');
                            setImageName('');
                          }}
                        >
                          <DeleteOutlined style={{ fontSize: 11 }} /> Remover
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <ImageCropModal
                  open={!!cropSrc}
                  src={cropSrc}
                  onCancel={closeCrop}
                  onDone={handleCropDone}
                />
              </div>
            </Col>

            <Col span={24}>
              <div className="mb-4">
                <p className="text-sm font-medium mb-2 text-gray-500 dark:text-[#6b8f6e]">
                  Texto base
                </p>
                <div className="border border-gray-200 dark:border-[#1a2e1c] rounded-lg px-4 py-3 bg-gray-50 dark:bg-[#0f1a10]">
                  <div className="flex flex-wrap items-end gap-3">
                    <Form.Item
                      name={["sharedTextId"]}
                      label="Texto compartilhado"
                      className="!mb-0 flex-1 min-w-[240px]"
                      tooltip="As questões que apontam para o mesmo texto aparecem sempre juntas e em sequência no carrossel."
                    >
                      <Select
                        showSearch
                        allowClear
                        placeholder="Nenhum — questão avulsa"
                        optionFilterProp="label"
                        options={sharedTexts.map((t) => ({
                          value: t.id,
                          label: `${t.title}${t.questionCount ? ` (${t.questionCount} questões)` : ''}`,
                        }))}
                      />
                    </Form.Item>

                    {sharedTextId && (
                      <Form.Item
                        name={["sharedTextOrder"]}
                        label="Ordem"
                        className="!mb-0 w-24"
                        tooltip="Posição desta questão dentro do grupo do texto."
                      >
                        {/* InputNumber (e não Input type=number) porque o Go
                            faz bind de shared_text_order em int — uma string
                            "2" devolveria 400. */}
                        <InputNumber min={1} placeholder="1" className="w-full" />
                      </Form.Item>
                    )}

                    <div className="flex items-center gap-2 pb-[2px]">
                      {sharedTextSelecionado && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingSharedText(sharedTextSelecionado);
                            setSharedTextModalOpen(true);
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-gray-100 dark:bg-[#1a2e1c] text-gray-700 dark:text-[#6b8f6e] hover:bg-gray-200 dark:hover:bg-[#243d26] shadow-sm transition-all cursor-pointer"
                        >
                          Editar texto
                        </button>
                      )}
                      {sharedTextId && (
                        <button
                          type="button"
                          onClick={() => {
                            form.setFieldsValue({
                              sharedTextId: undefined,
                              sharedTextOrder: undefined,
                            });
                            notification.info({
                              message: "Vínculo com o texto base removido.",
                              description:
                                "Salve a questão para gravar — o texto em si continua existindo para as demais questões.",
                            });
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-red-50 dark:bg-[#2e1a1a] text-red-600 dark:text-[#f87171] hover:bg-red-100 dark:hover:bg-[#3d2424] shadow-sm transition-all cursor-pointer"
                        >
                          Desvincular
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setEditingSharedText(null);
                          setSharedTextModalOpen(true);
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-lime-500 hover:bg-lime-600 text-black shadow-sm transition-all cursor-pointer"
                      >
                        Novo texto
                      </button>
                    </div>
                  </div>

                  {sharedTextSelecionado && (
                    <details className="mt-3">
                      <summary className="cursor-pointer text-xs text-gray-500 dark:text-[#6b8f6e]">
                        Pré-visualizar o texto
                      </summary>
                      <div className="mt-2">
                        <MarkdownEditor readOnly value={sharedTextSelecionado.body} />
                      </div>
                    </details>
                  )}
                </div>
              </div>
            </Col>

            {questionId && questionId !== 'undefined' && (
              <Col span={24}>
                <div className="mb-4">
                  <p className="text-sm font-medium mb-2 text-gray-500 dark:text-[#6b8f6e]">
                    Vínculo com PDF
                  </p>
                  <div className="border border-gray-200 dark:border-[#1a2e1c] rounded-lg px-4 py-3 bg-gray-50 dark:bg-[#0f1a10]">
                    <div className="flex items-center justify-between">
                      {highlight ? (
                        <span className="flex items-center gap-2 text-sm text-green-700 dark:text-[#82ca9d]">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-700 dark:bg-[#82ca9d] inline-block" />
                          Vinculado — Pg. {highlight.page} · {highlight.pdf_title || highlight.pdf_doc_id}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-[#6b8f6e]">Nenhum trecho de PDF vinculado</span>
                      )}
                      <div className="flex items-center gap-2">
                        {highlight && (
                          <button
                            type="button"
                            onClick={() => setPdfVisible(v => !v)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold shadow-sm transition-all cursor-pointer ${
                              pdfVisible
                                ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 ring-1 ring-blue-400'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                            }`}
                          >
                            {pdfVisible ? 'Ocultar PDF' : 'Ver PDF'}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setAnnotatorOpen(true)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-gray-100 dark:bg-[#1a2e1c] text-gray-700 dark:text-[#6b8f6e] hover:bg-gray-200 dark:hover:bg-[#243d26] shadow-sm transition-all cursor-pointer"
                        >
                          {highlight ? 'Editar vínculo' : 'Vincular PDF'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            )}

            <Col span={24}>
              <Form.Item label={"Enunciado"} name={["statement"]}>
                {/* <TextArea rows={4} placeholder='Ex.: O que é SQI?' /> */}
                <MarkdownEditor />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Informe as Tags" name={["keyword"]}>
                <Select
                  mode="tags"
                  placeholder="Selecione ou informe um item..."
                  style={{ width: "100%" }}
                  tokenSeparators={[","]} // corrigi para também separar por vírgula corretamente
                  onChange={handleKeywordsChange}
                >
                  {tagsKeywords?.map((elemento) => (
                    <Option key={elemento.description} value={elemento.description}>
                      {elemento.description}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <div className="flex flex-row justify-between">
                <Form.Item name={["difficulty"]}>
                  <Radio.Group
                    options={opcoesDeDificuldade}
                    optionType="button"
                    buttonStyle="solid"
                  />
                </Form.Item>
                <Button type="primary" danger onClick={toggleOutdated} className="font-semibold mr-4">Desatualizada</Button>
              </div>

              {showOutdated &&(
                <Form.Item
                  label={"Explique o motivo da questão estar desatualizada"}
                  className="bg-yellow-100 dark:bg-yellow-900/20 p-5 font-semibold"
                  name={["outdated"]}
                  rules={[
                    {
                      required: false,
                      message: "O campo é obrigatório",
                    },
                  ]}
                >
                  <TextArea rows={4} className="text-red-500" placeholder={`Descrição da alternativa `} />
                </Form.Item> )}
            </Col>
            
            {NOMES_DAS_ALTERNATIVAS.slice(
              0,
              temAlternativaE ? MAX_CHOICES : MAX_CHOICES - 1,
            ).map((nome, index) => {
              const opcional = index === MAX_CHOICES - 1;
              return (
                <React.Fragment key={index}>
                  <Col span={24}>
                    <div className="flex flex-row w-full justify-center items-center border border-1 rounded-md border-black dark:border-slate-600 mt-5">
                      <p className="text-4xl font-bold p-5">{numberToLetter(index).toUpperCase()}</p>
                      <div className="w-full mt-5">
                        {opcional && (
                          <div className="flex justify-end items-center gap-3 pr-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400">Alternativa opcional</span>
                            <Button size="small" danger onClick={removerAlternativaE}>
                              Remover alternativa E
                            </Button>
                          </div>
                        )}
                        <Form.Item
                          name={["choices", index, "description"]}
                          rules={[
                            {
                              required: !opcional,
                              message: "O campo é obrigatório",
                            },
                          ]}
                        >
                          <TextArea rows={4} placeholder={`Descrição da alternativa ${nome}`} />
                        </Form.Item>

                        <Form.Item
                          name={["choices", index, "correct"]}
                          rules={[
                            {
                              required: true,
                              message: "Por favor, selecione um item!",
                            },
                          ]}
                        >
                          <Radio.Group>
                            <Radio.Button
                              value={true}
                              className={`${choices?.[index]?.correct ?'bg-blue-500 text-white hover:text-white font-semibold':''}`}
                              onChange={() => {
                                const updatedChoices = choices?.map((choice : any, i:number) => ({
                                  ...choice,
                                  correct: i === index,
                                }));
                                form.setFieldsValue({ choices: updatedChoices });
                              }}
                            >
                              Correta
                            </Radio.Button>
                            <Radio.Button value={false}>Errada</Radio.Button>
                          </Radio.Group>
                          {/* Show explanation button only if this choice is correct */}
                          {choices?.[index]?.correct && (
                            <Button type="primary" className="ml-10 bg-green-500 font-semibold" onClick={toggleRationale}>Explicação da Resposta</Button>
                          )}
                        </Form.Item>
                        {(choices?.[index]?.correct && showRationale) && (
                          <Form.Item
                          label={"Explique o motivo da questão estar correta"}
                          className="bg-green-100 dark:bg-green-900/20 p-5 font-semibold"
                          name={["choices", index, "rationale"]}
                          rules={[
                            {
                              required: false,
                              message: "O campo é obrigatório",
                            },
                          ]}
                        >
                          <TextArea rows={4} placeholder={`Descrição da alternativa ${nome}`} />
                        </Form.Item>)}                    
                      </div>
                    </div>
                  </Col>
                </React.Fragment>
              );
            })}

            {!temAlternativaE && (
              <Col span={24} className="flex justify-center">
                <Button className="mt-5" type="dashed" onClick={adicionarAlternativaE}>
                  + Adicionar alternativa E
                </Button>
              </Col>
            )}

            <Col span={24} className="flex justify-end gap-3">
              <Button
                className="my-4"
                onClick={openPreview}
                disabled={previewDisabled}
              >
                Ver como no carrossel
              </Button>
              <Button className="my-4 item-end" type={"primary"} htmlType={"submit"}>
                Enviar dados
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
      {pdfVisible && highlight && (
        <div
          className="border-l border-gray-200 dark:border-[#1a2e1c] flex-shrink-0"
          style={{ width: '45%', minWidth: 340 }}
        >
          <PdfViewer
            fileKey={highlight.file_key ?? ''}
            page={highlight.page}
            rect={highlight.rect}
            reviewed={highlight.reviewed}
            onHide={() => setPdfVisible(false)}
          />
        </div>
      )}
      </div>

      <QuestionPreviewModal
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        data={previewData}
      />

      <SharedTextEditorModal
        open={sharedTextModalOpen}
        text={editingSharedText}
        onCancel={() => {
          setSharedTextModalOpen(false);
          setEditingSharedText(null);
        }}
        onSaved={handleSharedTextSaved}
      />

      {annotatorOpen && (
        <Modal
          open
          onCancel={() => {
            setAnnotatorOpen(false);
            if (questionId) PdfHighlightService.getByQuestion(questionId).then(h => setHighlight(h ?? null)).catch(() => {});
          }}
          width="90vw"
          footer={null}
          title="Vincular trecho de PDF"
          styles={{ body: { height: '80vh', padding: 0 } }}
        >
          <PdfAnnotator
            questionId={questionId}
            questionLabel={(questaoReturned || questao)?.statement?.slice(0, 60)}
            onSave={() => {
              PdfHighlightService.getByQuestion(questionId).then(h => setHighlight(h ?? null)).catch(() => {});
            }}
          />
        </Modal>
      )}
    </DefaultLayout>
  );
}
